import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit3, Save, X, Star, FileText } from 'lucide-react';
import { noteTemplatesApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface NoteTemplate {
  id: string;
  coach_id: string;
  name: string;
  description: string | null;
  structure: any;
  is_default: boolean;
  created_at: string;
}

interface TemplateSection {
  key: string;
  label: string;
  enabled: boolean;
}

const DEFAULT_SECTIONS: TemplateSection[] = [
  { key: 'vorb_intention', label: 'Vorbereitung: Intention', enabled: true },
  { key: 'vorb_methoden', label: 'Vorbereitung: Geplante Methoden', enabled: true },
  { key: 'vorb_offene_punkte', label: 'Vorbereitung: Offene Punkte', enabled: true },
  { key: 'checkin_befinden', label: 'Check-in: Befinden', enabled: true },
  { key: 'checkin_skala', label: 'Check-in: Skala (1-10)', enabled: true },
  { key: 'seit_letzter_sitzung', label: 'Seit letzter Sitzung', enabled: true },
  { key: 'hausaufgaben_erledigt', label: 'Hausaufgaben erledigt?', enabled: true },
  { key: 'thema', label: 'Thema der Sitzung', enabled: true },
  { key: 'methoden', label: 'Methoden', enabled: true },
  { key: 'durchfuehrung', label: 'Durchführung & Beobachtungen', enabled: true },
  { key: 'ergebnisse', label: 'Ergebnisse & Erkenntnisse', enabled: true },
  { key: 'vereinbarungen', label: 'Vereinbarungen', enabled: true },
  { key: 'hausaufgaben', label: 'Hausaufgaben', enabled: true },
  { key: 'checkout_befinden', label: 'Check-out: Befinden', enabled: true },
  { key: 'checkout_skala', label: 'Check-out: Skala (1-10)', enabled: true },
  { key: 'hausaufgaben_naechste', label: 'Vereinbarungen nächste Sitzung', enabled: true },
  { key: 'naechster_termin', label: 'Nächster Termin', enabled: true },
];

const makeEmptyForm = () => ({
  name: '',
  description: '',
  sections: DEFAULT_SECTIONS.map(s => ({ ...s })),
  is_default: false,
});

export default function NoteTemplateEditor() {
  const { showToast } = useToast();
  const [templates, setTemplates] = useState<NoteTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(makeEmptyForm());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await noteTemplatesApi.list();
      setTemplates(res.data || []);
    } catch {
      showToast('Vorlagen konnten nicht geladen werden.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openCreate = () => {
    setEditingId(null);
    setForm(makeEmptyForm());
    setShowEditor(true);
  };

  const openEdit = (t: NoteTemplate) => {
    setEditingId(t.id);
    // Merge saved structure with default sections (to cover new sections added later)
    const savedSections: TemplateSection[] = t.structure?.sections || [];
    const merged = DEFAULT_SECTIONS.map(def => {
      const saved = savedSections.find(s => s.key === def.key);
      return saved ? { ...def, enabled: saved.enabled } : { ...def };
    });
    setForm({
      name: t.name,
      description: t.description || '',
      sections: merged,
      is_default: t.is_default,
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showToast('Bitte einen Namen eingeben.', 'info');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        structure: { sections: form.sections },
        is_default: form.is_default,
      };
      if (editingId) {
        await noteTemplatesApi.update(editingId, payload);
        showToast('Vorlage aktualisiert.', 'success');
      } else {
        await noteTemplatesApi.create(payload);
        showToast('Vorlage erstellt.', 'success');
      }
      setShowEditor(false);
      loadTemplates();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Speichern.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    setDeleting(id);
    try {
      await noteTemplatesApi.delete(id);
      showToast('Vorlage gelöscht.', 'success');
      loadTemplates();
    } catch {
      showToast('Fehler beim Löschen.', 'error');
    } finally {
      setDeleting(null);
    }
  };

  const handleSetDefault = async (t: NoteTemplate) => {
    try {
      await noteTemplatesApi.update(t.id, { is_default: true });
      showToast(`"${t.name}" ist jetzt die Standardvorlage.`, 'success');
      loadTemplates();
    } catch {
      showToast('Fehler beim Setzen der Standardvorlage.', 'error');
    }
  };

  const toggleSection = (index: number) => {
    const updated = [...form.sections];
    updated[index] = { ...updated[index], enabled: !updated[index].enabled };
    setForm({ ...form, sections: updated });
  };

  if (loading) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
        Vorlagen werden geladen...
      </div>
    );
  }

  // Editor modal
  if (showEditor) {
    return (
      <div className="card" style={{ padding: 32, maxWidth: 720 }}>
        <div className="flex-between mb-16">
          <h3>{editingId ? 'Vorlage bearbeiten' : 'Neue Notiz-Vorlage'}</h3>
          <button className="btn-icon" onClick={() => setShowEditor(false)}>
            <X size={18} />
          </button>
        </div>

        <div className="form-group">
          <label>Name *</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="z.B. Standard-Coaching, Kurzprotokoll, Krisenintervention..."
          />
        </div>

        <div className="form-group">
          <label>Beschreibung</label>
          <input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Wofür wird diese Vorlage verwendet?"
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <input
            type="checkbox"
            id="is_default"
            checked={form.is_default}
            onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
            style={{ width: 18, height: 18 }}
          />
          <label htmlFor="is_default" style={{ cursor: 'pointer', marginBottom: 0 }}>
            Als Standardvorlage verwenden
          </label>
        </div>

        <h4 style={{ marginBottom: 8 }}>Sektionen</h4>
        <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
          Aktiviere oder deaktiviere Sektionen, die in dieser Vorlage angezeigt werden sollen.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {form.sections.map((section, index) => (
            <div
              key={section.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                background: section.enabled ? 'var(--bg)' : 'transparent',
                borderRadius: 6,
                cursor: 'pointer',
                opacity: section.enabled ? 1 : 0.5,
                transition: 'all 0.15s ease',
              }}
              onClick={() => toggleSection(index)}
            >
              <input
                type="checkbox"
                checked={section.enabled}
                onChange={() => toggleSection(index)}
                onClick={(e) => e.stopPropagation()}
                style={{ width: 16, height: 16 }}
              />
              <span style={{ fontSize: '0.88rem' }}>{section.label}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={16} /> {saving ? 'Speichern...' : 'Speichern'}
          </button>
          <button className="btn btn-secondary" onClick={() => setShowEditor(false)}>
            Abbrechen
          </button>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div>
      <div className="flex-between mb-16">
        <div>
          <h3 style={{ marginBottom: 4 }}>Notiz-Vorlagen</h3>
          <p className="text-muted text-sm">
            Definiere Vorlagen für Sitzungsnotizen. Bestimme, welche Sektionen angezeigt werden.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          <Plus size={16} /> Neue Vorlage
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <FileText size={32} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
          <p className="text-muted">Noch keine Notiz-Vorlagen erstellt.</p>
          <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={openCreate}>
            <Plus size={14} /> Erste Vorlage erstellen
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {templates.map((t) => {
            const sectionCount = (t.structure?.sections || []).filter((s: any) => s.enabled).length;
            const totalSections = (t.structure?.sections || []).length;
            return (
              <div key={t.id} className="card" style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{t.name}</span>
                      {t.is_default && (
                        <span className="badge badge-active" style={{ fontSize: '0.7rem' }}>
                          Standard
                        </span>
                      )}
                    </div>
                    {t.description && (
                      <p className="text-muted text-sm" style={{ margin: 0 }}>{t.description}</p>
                    )}
                    <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                      {sectionCount}/{totalSections} Sektionen aktiv
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {!t.is_default && (
                      <button
                        className="btn-icon"
                        title="Als Standard setzen"
                        onClick={() => handleSetDefault(t)}
                        style={{ padding: 6 }}
                      >
                        <Star size={14} />
                      </button>
                    )}
                    <button
                      className="btn-icon"
                      title="Bearbeiten"
                      onClick={() => openEdit(t)}
                      style={{ padding: 6 }}
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      className="btn-icon"
                      title="Löschen"
                      onClick={() => handleDelete(t.id)}
                      disabled={deleting === t.id}
                      style={{ padding: 6, color: 'var(--danger)' }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
